import React, { useState } from "react";
import {
  useGetAvailablePlans,
  useCreateCheckoutSession,
  // useCreateStripePortalSession,
  useCurrentUser,
  useGetOrganizations,
} from "utils/hooks";
import { Plan } from "types";
import Button from "../Button/Button";
import "./PlanSelection.styles.scss";
import { ORGANIZATION_USER_ROLES } from "utils/constants";

interface PlanSelectionProps {
  organizationId: string;
  onPlanSelected?: (planId: string) => void;
  onCancel?: () => void;
}

const PlanSelection: React.FC<PlanSelectionProps> = ({
  organizationId,
  onPlanSelected,
  onCancel,
}) => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly"
  );
  const { user: currentUser } = useCurrentUser();
  const {
    plans,
    loading: plansLoading,
    error: plansError,
  } = useGetAvailablePlans();
  const {
    createCheckoutSession,
    loading: checkoutLoading,
    error: checkoutError,
  } = useCreateCheckoutSession();
  // const {
  //   createStripePortalSession,
  //   loading: portalLoading,
  //   error: portalError,
  // } = useCreateStripePortalSession();

  const activeSubscription = currentUser?.activeOrganization?.subscription;
  const currentPlan = activeSubscription?.plan;

  const { organizations } = useGetOrganizations({});

  // Find the active organization
  const activeOrganization = organizations.find(
    (org) => org.uuid === currentUser?.activeOrganizationId
  );

  // Check if user is an organization owner by looking at the users array in the organization data
  const isOrganizationOwner =
    activeOrganization?.users?.some(
      (orgUser) =>
        orgUser.uuid === currentUser?.id &&
        orgUser.role === ORGANIZATION_USER_ROLES.OWNER
    ) || false;

  const formatLimit = (limit: number) => {
    return limit === 9999 ? "Unlimited" : `Up to ${limit}`;
  };

  const handlePlanClick = async (plan: Plan) => {
    if (plan.slug === currentPlan?.slug) {
      return;
    }

    if (activeSubscription) {
      // Redirect to Stripe customer portal
      const portalUrl = process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL;
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        console.error("Stripe customer portal URL not configured");
      }
    } else {
      const priceId =
        billingCycle === "annual"
          ? plan.stripe_annual_price_id
          : plan.stripe_price_id;

      if (!priceId) {
        console.error(
          `No Stripe price ID found for plan: ${plan.name} and billing cycle: ${billingCycle}`
        );
        return;
      }

      const successUrl = `${window.location.origin}/settings/subscription?upgrade=success`;
      const cancelUrl = `${window.location.origin}/settings/subscription?upgrade=cancelled`;

      const checkoutUrl = await createCheckoutSession({
        organizationId,
        priceId,
        successUrl,
        cancelUrl,
      });

      if (checkoutUrl) {
        onPlanSelected?.(plan.uuid);
        window.location.href = checkoutUrl;
      }
    }
  };

  if (!isOrganizationOwner) {
    return (
      <div className="plan-selection_access-msg">
        You do not manage the subscription for this practice. Please contact
        your practice administrator.
      </div>
    );
  }

  if (plansLoading) {
    return (
      <div className="plan-selection">
        <div className="plan-selection__loading">
          <p>Loading available plans...</p>
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="plan-selection">
        <div className="plan-selection__error">
          <p>Error loading plans: {plansError}</p>
          <Button
            name="retry"
            value="Retry"
            onClick={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="plan-selection">
      <div className="plan-selection__header">
        <h2>Choose Your Plan</h2>
        <p className="mb-2">Select a plan that best fits your needs</p>
        <div className="billing-cycle-toggle">
          <span>Monthly</span>
          <label className="switch">
            <input
              type="checkbox"
              onChange={() =>
                setBillingCycle(
                  billingCycle === "monthly" ? "annual" : "monthly"
                )
              }
            />
            <span className="slider round"></span>
          </label>
          <span>Annual (Save 20%)</span>
        </div>
      </div>

      <div className="plan-selection__plans">
        {plans.map((plan: Plan) => (
          <div
            key={plan.uuid}
            className={`plan-card ${
              plan.uuid === currentPlan?.uuid ? "plan-card--current" : ""
            }`}
          >
            <div className="plan-card__header">
              <h3>{plan.name}</h3>
              <div className="plan-card__price">
                <span className="plan-card__price-amount">
                  $
                  {billingCycle === "annual"
                    ? plan.price_per_seat_annual
                    : plan.price_per_seat}
                </span>
                <span className="plan-card__price-period">
                  {plan.slug === "reflective-practice" ? "/seat" : ""}/
                  {billingCycle === "annual" ? "year" : "month"}
                </span>
              </div>
            </div>

            <div className="plan-card__description">
              <p>{plan.description}</p>
            </div>

            <div className="plan-card__features">
              <h4>What&apos;s included:</h4>
              <ul>
                {plan.seat_limit > 0 && (
                  <li>{formatLimit(plan.seat_limit)} team members</li>
                )}
                {plan.practice_limit > 0 && (
                  <li>{formatLimit(plan.practice_limit)} practices</li>
                )}
                {plan.group_limit > 0 && (
                  <li>{formatLimit(plan.group_limit)} groups</li>
                )}
                {plan.patientLimit > 0 && (
                  <li>{formatLimit(plan.patientLimit)} patients</li>
                )}
                {plan.assignmentLimit > 0 && (
                  <li>{formatLimit(plan.assignmentLimit)} assignments</li>
                )}
                {plan.fileLimit > 0 && (
                  <li>{formatLimit(plan.fileLimit)} files</li>
                )}
                {plan.features?.map((feature: string, index: number) => (
                  <li key={index}>
                    {feature
                      .replace(/-/g, " ")
                      .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </li>
                ))}
              </ul>
            </div>

            <div className="plan-card__actions">
              <Button
                name={`select_${plan.slug}`}
                value={
                  plan.uuid === currentPlan?.uuid
                    ? "Current Plan"
                    : activeSubscription
                    ? "Change Plan"
                    : "Choose Plan"
                }
                onClick={() => handlePlanClick(plan)}
                disabled={
                  checkoutLoading ||
                  // portalLoading ||
                  plan.uuid === currentPlan?.uuid
                }
                fullWidth
              />
            </div>
          </div>
        ))}
      </div>

      {checkoutError && (
        <div className="plan-selection__error">
          <p>Error creating checkout session: {checkoutError}</p>
        </div>
      )}

      {/* {portalError && (
        <div className="plan-selection__error">
          <p>Error creating portal session: {portalError}</p>
        </div>
      )} */}

      {onCancel && (
        <div className="plan-selection__footer">
          <Button name="cancel" value="Cancel" onClick={onCancel} />
        </div>
      )}
    </div>
  );
};

export default PlanSelection;
